/**
 * Compose the `appContext` blob that ships in every chat request
 * body. The trunk agents read this from `RequestContext` and
 * inject validated fields into the system prompt under an
 * `<context>` block — so the agent knows exactly what the
 * operator is looking at without the operator having to spell
 * it out (campaign id, workflow id, venue serial, etc.).
 *
 * Conventions:
 *   - Always include `pathname` so the agent has a coarse "where
 *     in the app are we" hook even when no specific id is
 *     available.
 *   - Include any UUIDs / serials we can extract or that the host
 *     passes explicitly. Trunk validates each one before using it
 *     (UUID regex / length cap), so passing too much is fine; the
 *     agent simply ignores anything malformed.
 *   - Mirror the field names trunk's marketing-agent looks for:
 *     `campaignId`, `workflowId`, `segmentId`. Bookings / wifi /
 *     loyalty agents will follow the same naming when wired:
 *     `venueSerial`, `bookingId`, `loyaltyProgramId`, etc.
 *
 * Pages should call this once per render with whatever ids they
 * have in scope; missing fields fall back to URL parsing where
 * possible.
 */

interface ComposeArgs {
  pathname: string
  /** Extras the host has handy. Win over URL parsing. */
  campaignId?: string | null
  workflowId?: string | null
  segmentId?: string | null
  venueSerial?: string | null
  deviceSerial?: string | null
  bookingId?: string | null
}

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i

function uuidAfter(pathname: string, segment: string): string | null {
  const re = new RegExp(`/${segment}/(${UUID_RE.source})(?:/|$)`, 'i')
  const m = pathname.match(re)
  return m?.[1] ?? null
}

export function composeAppContext({
  pathname,
  campaignId,
  workflowId,
  segmentId,
  venueSerial,
  deviceSerial,
  bookingId,
}: ComposeArgs): Record<string, unknown> {
  const ctx: Record<string, unknown> = { pathname }

  const c = campaignId ?? uuidAfter(pathname, 'campaigns')
  if (c) ctx.campaignId = c

  const w = workflowId ?? uuidAfter(pathname, 'workflows')
  if (w) ctx.workflowId = w

  const s = segmentId ?? uuidAfter(pathname, 'segments')
  if (s) ctx.segmentId = s

  const venueMatch = /\/venues\/([^/]+)/i.exec(pathname)
  const v = venueSerial ?? venueMatch?.[1] ?? null
  if (v) ctx.venueSerial = v

  const d = deviceSerial?.trim()
  if (d) ctx.deviceSerial = d

  const wifiMatch = /\/wi-fi(?:\/([^?#]+))?/i.exec(pathname)
  if (wifiMatch) {
    ctx.wifiSection =
      wifiMatch[1]?.split('/').filter(Boolean).join('/') || 'overview'
  }

  const snapGuardMatch = /\/wi-fi\/snap-guard(?:\/([^?#]+))?/i.exec(pathname)
  if (snapGuardMatch) {
    ctx.snapGuardView =
      snapGuardMatch[1]?.split('/').filter(Boolean).join('/') || 'dashboard'
  }

  const bookingMatch = /\/bookings\/([0-9a-f-]{8,})(?:\/|$)/i.exec(pathname)
  const b = bookingId ?? bookingMatch?.[1] ?? null
  if (b) ctx.bookingId = b

  return ctx
}
